<template>
  <div
    class="margin-top"
    data-tour-id="issued-invoices-date-range-filter"
  >
    <div class="margin-top-half">
      <div class="label-input">{{ $t('issuedInvoiceDate') }}</div>
      <div
        class="recepDate marginTop10"
        :class="{
          'input-error': showDateError || showDateRequiredError,
        }"
      >
        <f7-input
          ref="datePicker"
          class="shadow"
          type="datepicker"
          :placeholder="$t('issuedInvoiceDate')"
          :clear-button="true"
          :value="calculatedInitialDate"
          :calendar-params="calendarParams"
          :error-message="
            showDateRequiredError
              ? $t('requiredField')
              : showDateError
              ? $t('issuedInvoiceDateRangeMessage')
              : ''
          "
          :error-message-force="
            showDateRequiredError || showDateError
          "
          @calendar:change="setInitialDate"
          @input:clear="onClearDate"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import moment from 'moment';

export default {
  data() {
    return {
      showDateError: false,
      showDateRequiredError: false,
      pendingClearTimer: null,
    };
  },

  computed: {
    ...mapState('issuedInvoicesFilter', [
      'initialFromDate',
      'initialToDate',
    ]),

    calculatedInitialDate() {
      return this.initialFromDate && this.initialToDate
        ? [this.initialFromDate, this.initialToDate]
        : [];
    },

    calendarParams() {
      const companyPrefix = this.company?.substring(0, 2) || '';
      const format =
        companyPrefix === 'US' ? 'mm/dd/yyyy' : 'dd/mm/yyyy';
      return {
        closeOnSelect: true,
        rangePicker: true,
        dateFormat: format,
      };
    },
  },

  methods: {
    ...mapActions('issuedInvoicesFilter', [
      'setInitialFromDate',
      'setInitialToDate',
    ]),

    // Simula la acción de borrar del input, como si se pulsara la X
    clearCalendarInput() {
      const wrap = this.$refs.datePicker?.$el;
      if (!wrap) return;

      const clearBtn = wrap.querySelector('.input-clear-button');
      if (clearBtn) {
        clearBtn.click();
        return;
      }

      const inputEl = wrap.querySelector('input');
      if (inputEl) {
        inputEl.value = '';
        inputEl.dispatchEvent(new Event('input', { bubbles: true }));
        inputEl.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },

    setInitialDate(values) {
      const nextFrom =
        Array.isArray(values) && values[0] ? values[0] : null;
      const nextTo =
        Array.isArray(values) && values[1] ? values[1] : null;

      // REQUIRED: si falta una parte del rango
      if (!nextFrom || !nextTo) {
        // Si el store está vacío, muestra required
        if (!this.initialFromDate || !this.initialToDate) {
          this.showDateRequiredError = true;
          this.$notifyDX(
            {
              message: this.$t('requiredField'),
              width: 400,
            },
            'error',
            2000
          );
        }
        return;
      }
      // Ya hay rango completo => required OK
      this.showDateRequiredError = false;

      const diffInDays = moment(nextTo).diff(
        moment(nextFrom),
        'days'
      );
      const MAX_RANGE_DAYS = 730;

      if (diffInDays > MAX_RANGE_DAYS) {
        // Cancela un temporizador anterior si el usuario vuelve a seleccionar
        this.showDateRequiredError = false;
        if (this.pendingClearTimer) {
          clearTimeout(this.pendingClearTimer);
          this.pendingClearTimer = null;
        }

        // Activa el borde rojo y lanza el mensaje
        this.showDateError = true;
        this.$notifyDX(
          {
            message: this.$t('issuedInvoiceDateRangeMessage'),
            width: 400,
          },
          'error',
          5000
        );

        // Limpia el store y mantiene el input visible un momento
        this.setInitialFromDate(null);
        this.setInitialToDate(null);

        // Limpieza visual tras 4 segundos
        this.pendingClearTimer = setTimeout(() => {
          this.clearCalendarInput();
          this.showDateError = false;
          this.showDateRequiredError = true;
          this.pendingClearTimer = null;
        }, 4000);

        return;
      }

      // Si el rango es válido, cancela errores pendientes
      if (this.pendingClearTimer) {
        clearTimeout(this.pendingClearTimer);
        this.pendingClearTimer = null;
      }
      this.showDateError = false;

      const curFromTs = this.initialFromDate?.getTime() ?? null;
      const curToTs = this.initialToDate?.getTime() ?? null;
      const nextFromTs = nextFrom.getTime();
      const nextToTs = nextTo.getTime();

      if (curFromTs === nextFromTs && curToTs === nextToTs) return;

      this.setInitialFromDate(nextFrom);
      this.setInitialToDate(nextTo);
    },
    onClearDate() {
      this.setInitialFromDate(null);
      this.setInitialToDate(null);
      this.showDateError = false;
      this.showDateRequiredError = true; // queda vacío => required
    },
    validateRequiredRange() {
      const hasBoth = !!(this.initialFromDate && this.initialToDate);
      this.showDateRequiredError = !hasBoth;
      return hasBoth;
    },
  },
};
</script>

<style scoped lang="scss">
.input-error {
  border: 2px solid #ff4c4c !important;
  transition: border-color 0.3s ease;
}
</style>
